import { fetchWithOOB } from "./htmx-helpers";

interface TargetConfig {
  name: string;
  target_format: string;
  quality: number;
  target_width: string | number;
  target_height: string | number;
  lockRatio: boolean;
  ratio: number;
}

export const registerDuplicateModal = (Alpine: any) => {
  Alpine.data("duplicateModal", (deliverableID: string) => ({
    deliverableID: deliverableID,
    sourceGroupID: null as string | null,
    sourceGroupName: "",
    targets: [] as TargetConfig[],
    originalWidth: 0,
    originalHeight: 0,
    globalRatio: 0,
    isLoadingRatio: false,

    init() {
      // No-op init, logic is triggered by open()
    },

    async open(id: string, name: string) {
      this.sourceGroupID = id;
      this.sourceGroupName = name;
      this.targets = [];
      this.isLoadingRatio = true;

      try {
        // Fetch group metadata for aspect ratio
        const resp = await fetch(`/api/deliverables/${this.deliverableID}/groups/${id}/metadata`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.ratio > 0) {
            this.globalRatio = data.ratio;
            this.originalWidth = data.width;
            this.originalHeight = data.height;
          }
        }
      } catch (err) {
        console.error("[duplicateModal] Failed to fetch group metadata:", err);
      } finally {
        this.isLoadingRatio = false;
        // Add first target by default
        this.addTarget();
      }

      (document.getElementById("duplicate-group-modal") as any)?.showModal();
    },

    addTarget() {
      const count = this.targets.length;
      const suffix = count === 0 ? " (Copy)" : ` (Copy ${count + 1})`;
      this.targets.push({
        name: this.sourceGroupName + suffix,
        target_format: "jpeg",
        quality: 85,
        target_width: "",
        target_height: "",
        lockRatio: true,
        ratio: this.globalRatio,
      });
    },

    removeTarget(index: number) {
      this.targets.splice(index, 1);
      if (this.targets.length === 0) {
        this.addTarget();
      }
    },

    updateWidth(index: number) {
      const t = this.targets[index];
      const w = parseFloat(String(t.target_width));
      if (t.lockRatio && t.ratio > 0 && w) {
        t.target_height = Math.round(w / t.ratio);
      } else if (w && t.target_height) {
        t.ratio = w / parseFloat(String(t.target_height));
      }
    },

    updateHeight(index: number) {
      const t = this.targets[index];
      const h = parseFloat(String(t.target_height));
      if (t.lockRatio && t.ratio > 0 && h) {
        t.target_width = Math.round(h * t.ratio);
      } else if (t.target_width && h) {
        t.ratio = parseFloat(String(t.target_width)) / h;
      }
    },

    toggleLock(index: number) {
      const t = this.targets[index];
      t.lockRatio = !t.lockRatio;
      if (t.lockRatio && t.target_width && t.target_height) {
        t.ratio = parseFloat(String(t.target_width)) / parseFloat(String(t.target_height));
      }
    },

    setPreset(index: number, width: number) {
      const t = this.targets[index];
      t.target_width = width;
      if (t.ratio > 0) {
        t.target_height = Math.round(width / t.ratio);
      } else {
        t.target_height = "";
      }
    },

    resetOriginal(index: number) {
      const t = this.targets[index];
      t.target_width = "";
      t.target_height = "";
    },

    async submit() {
      if (!this.sourceGroupID) return;

      const payload = {
        source_group_id: this.sourceGroupID,
        targets: this.targets.map((t) => ({
          name: t.name,
          target_format: t.target_format,
          quality: parseInt(String(t.quality)) || 85,
          target_width: parseInt(String(t.target_width)) || 0,
          target_height: parseInt(String(t.target_height)) || 0,
        })),
      };

      await fetchWithOOB(
        "POST",
        "/api/deliverables/" + this.deliverableID + "/groups/batch-duplicate",
        payload,
      );

      (document.getElementById("duplicate-group-modal") as any)?.close();
    },
  }));
};
